import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { buildPath } from './lib/tenant';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Container, Header, Loader, Dimmer, Message, Segment, Icon, Grid, Label, Divider, Button, Confirm, Popup,
} from 'semantic-ui-react';
import { QRCodeSVG } from 'qrcode.react';
import moment from 'moment';
import { getOrderDetail, cancelOrder } from './lib/tickets';
import { t } from './locales';
import { formatMoney } from './lib/currency';

function addToCalendar(order) {
    const start = moment(order.event_date).format('YYYYMMDDTHHmmss');
    const end = moment(order.event_date).add(2, 'hours').format('YYYYMMDDTHHmmss');
    const ics = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//Selwyn Ticketing//EN',
        'BEGIN:VEVENT',
        `SUMMARY:${order.event_name}`,
        `DTSTART:${start}`,
        `DTEND:${end}`,
        `LOCATION:${order.event_location || ''}`,
        `DESCRIPTION:Order ref: ${order.order_ref}`,
        'END:VEVENT',
        'END:VCALENDAR',
    ].join('\r\n');
    const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${order.event_name.replace(/[^a-z0-9]/gi, '_')}.ics`;
    a.click();
    URL.revokeObjectURL(url);
}

const STATUS_CONFIG = {
    paid:      { label: () => t('ticketDetail.statusConfirmed'), color: 'var(--colour_heading_success)', icon: 'check circle' },
    pending:   { label: () => t('ticketDetail.statusPending'),   color: '#f0ad4e',                       icon: 'clock outline' },
    cancelled: { label: () => t('ticketDetail.statusCancelled'), color: '#bbb',                          icon: 'times circle outline' },
    refunded:  { label: () => t('ticketDetail.statusRefunded'),  color: '#bbb',                          icon: 'undo' },
};

function MaterialsSection({ title, iconName, accentColor, text, links, files }) {
    const hasContent = text || links?.length > 0 || files?.length > 0;
    if (!hasContent) return null;
    return (
        <Segment style={{ borderRadius: 12, border: `1px solid ${accentColor}22`, marginTop: '1.5rem' }}>
            <Header as="h4" style={{ color: accentColor, marginBottom: '0.75rem' }}>
                <Icon name={iconName} />
                {title}
            </Header>
            {text && (
                <p style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem', color: '#444', marginBottom: links?.length || files?.length ? '1rem' : 0 }}>
                    {text}
                </p>
            )}
            {links?.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {links.map((link, i) => (
                        <a
                            key={i}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                                color: 'var(--colour_link)', fontSize: '0.9rem', textDecoration: 'none',
                            }}
                        >
                            <Icon name="linkify" style={{ margin: 0 }} />
                            {link.title || link.url}
                        </a>
                    ))}
                </div>
            )}
            {files?.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: links?.length ? '0.5rem' : 0 }}>
                    {files.map((file, i) => (
                        <a
                            key={i}
                            href={typeof file === 'string' ? file : file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                                color: 'var(--colour_link)', fontSize: '0.9rem', textDecoration: 'none',
                            }}
                        >
                            <Icon name="file outline" style={{ margin: 0 }} />
                            {typeof file === 'string' ? file.split('/').pop() : (file.title || file.url?.split('/').pop())}
                        </a>
                    ))}
                </div>
            )}
        </Segment>
    );
}

function EventMaterials({ order }) {
    const isPast = order.event_date && moment(order.event_date).isBefore(moment());
    const hasPre = order.preparation_materials || order.preparation_links?.length > 0 || order.preparation_files?.length > 0;
    const hasPost = order.post_event_materials || order.post_event_links?.length > 0 || order.post_event_files?.length > 0;

    if (!hasPre && !hasPost) return null;

    return (
        <>
            {!isPast && hasPre && (
                <MaterialsSection
                    title={t('ticketDetail.beforeEvent')}
                    iconName="calendar check outline"
                    accentColor="var(--colour_menu_main)"
                    text={order.preparation_materials}
                    links={order.preparation_links}
                    files={order.preparation_files}
                />
            )}
            {isPast && hasPost && (
                <MaterialsSection
                    title={t('ticketDetail.eventResources')}
                    iconName="archive"
                    accentColor="#5a5a8a"
                    text={order.post_event_materials}
                    links={order.post_event_links}
                    files={order.post_event_files}
                />
            )}
        </>
    );
}

function TicketDetail() {
    const { orderRef } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [cancelMsg, setCancelMsg] = useState(null);
    const [qrFullscreen, setQrFullscreen] = useState(null);
    const [shareCopied, setShareCopied] = useState(false);

    const { data: order, isLoading, isError } = useQuery({
        queryKey: ['order', orderRef],
        queryFn: () => getOrderDetail(orderRef),
    });

    const cancelMutation = useMutation({
        mutationFn: cancelOrder,
        onSuccess: (result) => {
            setConfirmOpen(false);
            if (result.error) {
                setCancelMsg({ type: 'error', text: result.error });
            } else {
                queryClient.invalidateQueries(['order', orderRef]);
                queryClient.invalidateQueries(['my-orders']);
                setCancelMsg({ type: 'success', text: t('ticketDetail.cancelSuccessMessage') });
            }
        },
        onError: () => {
            setConfirmOpen(false);
            setCancelMsg({ type: 'error', text: t('ticketDetail.cancelFailedMessage') });
        },
    });

    const handleShare = (order) => {
        // Build a referral URL pointing to the event with this order's ref as the referral code
        const base = window.location.origin + window.location.pathname.split('/my-tickets')[0];
        const shareUrl = `${base}/events?ref=${order.order_ref}`;
        const shareText = t('ticketDetail.shareText', { eventName: order.event_name });

        if (navigator.share) {
            navigator.share({ title: order.event_name, text: shareText, url: shareUrl }).catch(() => {});
        } else {
            navigator.clipboard.writeText(shareUrl).then(() => {
                setShareCopied(true);
                setTimeout(() => setShareCopied(false), 2500);
            });
        }
    };

    if (isLoading) {
        return (
            <Dimmer active inverted page>
                <Loader size="large" content={t('ticketDetail.loading')} />
            </Dimmer>
        );
    }

    if (isError || order?.error) {
        return (
            <Container className="contentContainer">
                <Message negative>
                    <Message.Header>{t('ticketDetail.notFoundTitle')}</Message.Header>
                </Message>
                <Link to={buildPath('/my-tickets')}>{t('ticketDetail.backToMyTickets')}</Link>
            </Container>
        );
    }

    const isCancellable = order.status === 'paid' || order.status === 'pending';
    const isCancelled = order.status === 'cancelled' || order.status === 'refunded';
    const isPaid = order.status === 'paid';
    const statusCfg = STATUS_CONFIG[order.status] ?? STATUS_CONFIG.pending;

    return (
        <>
            {/* Fullscreen QR overlay */}
            {qrFullscreen && (
                <div
                    onClick={() => setQrFullscreen(null)}
                    style={{
                        position: 'fixed', inset: 0,
                        background: 'rgba(0,0,0,0.92)',
                        display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center',
                        zIndex: 9999, cursor: 'pointer',
                    }}
                >
                    <div style={{
                        background: '#fff', padding: 24, borderRadius: 16,
                        boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
                    }}>
                        <QRCodeSVG value={qrFullscreen} size={280} level="M" />
                    </div>
                    <p style={{ color: 'rgba(255,255,255,0.6)', marginTop: '1.5rem', fontSize: '0.9rem' }}>
                        {t('ticketDetail.tapToClose')}
                    </p>
                </div>
            )}

            <Container className="contentContainer" style={{ maxWidth: 700 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                    <Link to={buildPath('/my-tickets')}>
                        <Icon name="arrow left" />
                        {t('ticketDetail.myTicketsLink')}
                    </Link>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        {order.event_date && !isCancelled && (
                            <Button
                                basic
                                size="small"
                                icon="calendar plus"
                                content={t('ticketDetail.addToCalendar')}
                                onClick={() => addToCalendar(order)}
                            />
                        )}
                        {isPaid && (
                            <Button
                                basic
                                size="small"
                                icon="exchange"
                                content={t('ticketDetail.transfer')}
                                onClick={() => navigate(buildPath(`/transfer/${orderRef}`))}
                                title={t('ticketDetail.transferTitle')}
                            />
                        )}
                    </div>
                </div>

                <Header as="h1" className="sectionHeader">{order.event_name}</Header>

                {order.event_date && (
                    <p style={{ color: '#555', marginBottom: '0.25rem' }}>
                        <Icon name="calendar alternate outline" />
                        {moment(order.event_date).format('dddd D MMMM YYYY, HH:mm')}
                    </p>
                )}
                {order.event_location && (
                    <p style={{ color: '#555', marginBottom: '1.5rem' }}>
                        <Icon name="map marker alternate" />
                        {order.event_location}
                    </p>
                )}

                {cancelMsg && (
                    <Message
                        positive={cancelMsg.type === 'success'}
                        negative={cancelMsg.type === 'error'}
                        onDismiss={() => setCancelMsg(null)}
                        style={{ marginBottom: '1rem' }}
                    >
                        {cancelMsg.text}
                        {cancelMsg.type === 'success' && order.status === 'paid' && (
                            <p style={{ marginTop: '0.4rem', fontSize: '0.9rem', opacity: 0.8 }}>
                                {t('ticketDetail.refundNote')}
                            </p>
                        )}
                    </Message>
                )}

                <Segment style={{ borderRadius: 12, padding: '1.5rem', marginBottom: '1rem', background: '#f9f9f9' }}>
                    {/* Status badge — prominent */}
                    <div style={{
                        display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                        backgroundColor: statusCfg.color, color: '#fff',
                        borderRadius: 8, padding: '0.5rem 1rem',
                        fontWeight: 700, fontSize: '1rem',
                        marginBottom: '1.25rem',
                    }}>
                        <Icon name={statusCfg.icon} style={{ margin: 0 }} />
                        {statusCfg.label()}
                    </div>

                    <Grid columns={2} stackable>
                        <Grid.Column>
                            <p style={{ margin: 0 }}><strong>{t('ticketDetail.orderReference')}</strong></p>
                            <p style={{ margin: '0.25rem 0 0', fontFamily: 'monospace' }}>{order.order_ref}</p>
                        </Grid.Column>
                        <Grid.Column>
                            <p style={{ margin: 0 }}><strong>{t('ticketDetail.ticketsLabel')}</strong></p>
                            <p style={{ margin: '0.25rem 0 0' }}>
                                {order.quantity > 1
                                    ? t('ticketDetail.ticketCountPlural', { quantity: order.quantity })
                                    : t('ticketDetail.ticketCountSingular', { quantity: order.quantity })} — {formatMoney(order.total_amount, order.currency)}
                            </p>
                        </Grid.Column>
                        <Grid.Column>
                            <p style={{ margin: 0 }}><strong>{t('ticketDetail.purchased')}</strong></p>
                            <p style={{ margin: '0.25rem 0 0' }}>
                                {moment(order.created_at).format('D MMM YYYY')}
                            </p>
                        </Grid.Column>
                    </Grid>

                    {isCancellable && (
                        <div style={{ marginTop: '1.25rem', borderTop: '1px solid #e5e5e5', paddingTop: '1rem' }}>
                            <Button
                                basic
                                negative
                                size="small"
                                onClick={() => setConfirmOpen(true)}
                                loading={cancelMutation.isLoading}
                            >
                                {t('ticketDetail.cancelBooking')}
                            </Button>
                        </div>
                    )}

                    {order.status === 'refunded' && (
                        <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#888' }}>
                            <Icon name="info circle" />
                            {t('ticketDetail.refundReturnedNote')}
                        </p>
                    )}
                </Segment>

                {!isCancelled && (
                    <>
                        <Header as="h3" style={{ color: 'var(--colour_menu_main)', marginTop: '2rem', marginBottom: '0.5rem' }}>
                            {t('ticketDetail.yourTickets')}
                        </Header>
                        <p style={{ color: '#888', fontSize: '0.85rem', marginBottom: '1rem' }}>
                            {t('ticketDetail.tapToEnlargeHint')}
                        </p>

                        {(order.items ?? []).map((item, index) => (
                            <Segment key={item.id} className="ticketCard" style={{ borderRadius: 12, marginBottom: '1rem' }}>
                                <Grid stackable verticalAlign="middle">
                                    <Grid.Column width={10}>
                                        <p style={{ fontWeight: 700, marginBottom: '0.25rem' }}>
                                            {t('ticketDetail.ticketOfTotal', { index: index + 1, total: order.quantity })}
                                        </p>
                                        <p className="ticketCode">
                                            {item.ticket_code}
                                        </p>
                                        {item.is_used && (
                                            <Label color="grey" size="small">
                                                <Icon name="check" />
                                                {t('ticketDetail.usedAt', { date: item.used_at ? moment(item.used_at).format('D MMM YYYY HH:mm') : '' })}
                                            </Label>
                                        )}
                                    </Grid.Column>
                                    <Grid.Column width={6} textAlign="center">
                                        <div
                                            onClick={() => setQrFullscreen(item.ticket_code)}
                                            title={t('ticketDetail.tapToEnlarge')}
                                            style={{
                                                display: 'inline-block',
                                                padding: 12,
                                                background: '#fff',
                                                borderRadius: 8,
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                                cursor: 'zoom-in',
                                            }}
                                        >
                                            <QRCodeSVG
                                                value={item.ticket_code}
                                                size={120}
                                                level="M"
                                            />
                                        </div>
                                        <p style={{ fontSize: '0.75rem', color: '#aaa', marginTop: '0.4rem' }}>
                                            {t('ticketDetail.tapToEnlarge')}
                                        </p>
                                    </Grid.Column>
                                </Grid>

                                {index < (order.items.length - 1) && <Divider />}
                            </Segment>
                        ))}
                    </>
                )}

                {/* Pre / post event materials */}
                {isPaid && <EventMaterials order={order} />}

                {/* Share with friends section */}
                {isPaid && (
                    <Segment
                        style={{
                            borderRadius: 12,
                            background: 'linear-gradient(135deg, #f5f0ff, #eef)',
                            border: '1px solid #d6c8ff',
                            marginTop: '2rem',
                        }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
                            <div>
                                <p style={{ margin: 0, fontWeight: 700, color: 'var(--colour_menu_main)' }}>
                                    <Icon name="share alternate" />
                                    {t('ticketDetail.spreadTheWord')}
                                </p>
                                <p style={{ margin: '0.25rem 0 0', fontSize: '0.9rem', color: '#666' }}>
                                    {t('ticketDetail.shareDescription')}
                                </p>
                            </div>
                            <Popup
                                content={shareCopied ? t('ticketDetail.linkCopied') : t('ticketDetail.shareThisEvent')}
                                open={shareCopied || undefined}
                                position="top right"
                                trigger={
                                    <Button
                                        style={{ backgroundColor: 'var(--colour_menu_main)', color: '#fff', borderRadius: 8 }}
                                        size="small"
                                        onClick={() => handleShare(order)}
                                    >
                                        <Icon name="share alternate" />
                                        {t('ticketDetail.shareEventButton')}
                                    </Button>
                                }
                            />
                        </div>
                    </Segment>
                )}

                <Confirm
                    open={confirmOpen}
                    header={t('ticketDetail.cancelBookingHeader')}
                    content={t('ticketDetail.cancelBookingConfirmContent')}
                    confirmButton={t('ticketDetail.confirmCancel')}
                    cancelButton={t('ticketDetail.keepBooking')}
                    onCancel={() => setConfirmOpen(false)}
                    onConfirm={() => cancelMutation.mutate(order.order_ref)}
                />
            </Container>
        </>
    );
}

export default TicketDetail;
